import React, { useEffect, useState } from 'react';
import posthog from 'posthog-js';

import { ModeCard } from '../components/common/ModeCard';
import { useActiveLeague } from '../hooks/useActiveLeague';
import quickPlayIcon from '../assets/icons/quick-play-icon.svg';
import createLeagueIcon from '../assets/icons/create-league-icon.svg';
import switchLeagueIcon from '../assets/icons/switch-league-icon.svg';

const TABS = { QUICKPLAY: 'quickplay', LEAGUE: 'league' };

// Quick Play leagues may exist as a slug-only stub before gameMode loads, so
// the slug prefix covers that early state (same heuristic as Header.jsx).
function isQuickplayLeague(league) {
    return league?.gameMode === 'quickplay' ||
        (!league?.gameMode && String(league?.slug || '').startsWith('quick-play-'));
}

// Guests never appear in the /leagues member listing, so on slug-less routes
// the active league can stay null even mid Quick Play. The persisted slug
// still identifies the league they were browsing.
function getStoredLeagueSlug() {
    try {
        return localStorage.getItem('lastActiveLeagueSlug');
    } catch (e) {
        return null;
    }
}

function MorePage() {
    const { activeLeague } = useActiveLeague();
    const [selectedTab, setSelectedTab] = useState(null);
    // Default tab cross-sells the other mode: quickplay players see League,
    // league players see Quick Play. A click always wins over the default.
    const defaultLeague = activeLeague || { slug: getStoredLeagueSlug() };
    const activeTab = selectedTab ?? (isQuickplayLeague(defaultLeague) ? TABS.LEAGUE : TABS.QUICKPLAY);

    useEffect(() => {
        posthog.capture('more_viewed');
    }, []);

    return (
        <div className="more-page">

            <div className="wrapper">

                <h1 className="mode-display-title">
                    {activeTab === TABS.QUICKPLAY ? 'QUICK PLAY MODE' : 'LEAGUE MODE'}
                </h1>

                <div className="onpage-nav-block">
                    <ul className="onpage-nav" role="tablist" aria-label="Game modes">
                        <li role="presentation" className={activeTab === TABS.QUICKPLAY ? 'active' : ''}>
                            <button
                                type="button"
                                role="tab"
                                id="more-tab-quickplay"
                                aria-controls="more-panel-quickplay"
                                aria-selected={activeTab === TABS.QUICKPLAY}
                                onClick={() => setSelectedTab(TABS.QUICKPLAY)}
                            >
                                Quick Play
                            </button>
                        </li>
                        <li role="presentation" className={activeTab === TABS.LEAGUE ? 'active' : ''}>
                            <button
                                type="button"
                                role="tab"
                                id="more-tab-league"
                                aria-controls="more-panel-league"
                                aria-selected={activeTab === TABS.LEAGUE}
                                onClick={() => setSelectedTab(TABS.LEAGUE)}
                            >
                                League
                            </button>
                        </li>
                    </ul>
                </div>

                {activeTab === TABS.QUICKPLAY ? (
                    <div
                        className="mode-cards mode-cards--single"
                        role="tabpanel"
                        id="more-panel-quickplay"
                        aria-labelledby="more-tab-quickplay"
                    >
                        <ModeCard
                            to="/play"
                            icon={quickPlayIcon}
                            title="PLAY NOW"
                            description="Play live matches with a ready-made squad whenever you want."
                        />
                    </div>
                ) : (
                    <div
                        className="mode-cards"
                        role="tabpanel"
                        id="more-panel-league"
                        aria-labelledby="more-tab-league"
                    >
                        <ModeCard
                            to="/play-with-friends"
                            icon={createLeagueIcon}
                            title="CREATE LEAGUE"
                            description="Create your own league and invite others to play a classic season with matches, transfers and player development."
                            highlighted
                        />
                        <ModeCard
                            to="/switch-league"
                            icon={switchLeagueIcon}
                            title="SWITCH LEAGUE"
                            description="Switch between your existing leagues."
                        />
                    </div>
                )}

            </div>

        </div>
    );
}

export { MorePage };
